import { useEffect, useMemo, useState } from 'react';
import { addDoc, collection, doc, onSnapshot, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { coachLinkUrl, createCoachLink, isLiveCoachLink, revokeCoachLink } from '../utils/coachLinks';
import useTourTeams from '../hooks/useTourTeams';
import { addTeamsForUnlinkedSquads, linkTeamToSquad, unlinkedSquads } from '../utils/teamFixtures';
import { downloadTeamCategoriesPng } from '../utils/teamCategoriesImage';

// ── Teams / sub-groups ───────────────────────────────────────────────────────
// Splits a tour's players into teams (age groups, A/B squads, whatever the
// tour needs), using the same tap-or-drag pattern as rooming.
//
//   1. MST staff create the teams           (mode 'staff')
//   2. Staff or the coach name/rename them  (both modes)
//   3. The coach places and moves players   (both modes)
//
// Stored at requests/{id}/teams/layout as { teams: [{id, name, tourLeaderId,
// tourLeaderName}],
// assignments: { [teamId]: [participantId] }, coachToken }. Every change saves
// straight away — there's no submit step, so staff always see the current
// split. Coaches who don't sign in use the emailed no-login link
// (/teams-assignment, via coachLinks — see functions/coachLinks.js), which
// renders the same TeamsBoard.
//
// Staff can put one of the tour's tour leaders in charge of each team; that
// leader sees their team highlighted and listed first in their portal.
//
// Teams also come from Team Info: every squad on the customer's form gets a
// team automatically (squadId + a cached ageLabel), so the coach can name it
// knowing the age group before any players are registered. See
// utils/teamFixtures.js.

export function teamsLayoutRef(requestId) {
  return doc(db, 'requests', requestId, 'teams', 'layout');
}

function fullName(p) {
  return [p.firstName, p.surname].filter(Boolean).join(' ') || p.email || '—';
}

/** "12/03/2011 · Midfielder" — date of birth and playing position, whichever are given. */
export function playerDetails(p) {
  return [formatDob(p), p?.position].filter(Boolean).join(' · ');
}

/** A participant's date of birth as DD/MM/YYYY, or '' when not given. */
export function formatDob(p) {
  const parts = String(p?.dateOfBirth || '').split('-');
  return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : (p?.dateOfBirth || '');
}

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

// Participants without a traveller type predate the field and were almost
// always players, so they count as players.
export function isPlayer(p) {
  return !p.travellerType || String(p.travellerType).toLowerCase() === 'player';
}

/** Move a participant into a team (or out of every team when teamId is null). */
export function moveParticipant(assignments, participantId, teamId) {
  const next = {};
  for (const [tid, pids] of Object.entries(assignments || {})) {
    next[tid] = (pids || []).filter((pid) => pid !== participantId);
  }
  if (teamId) next[teamId] = [...(next[teamId] || []), participantId];
  return next;
}

/** The tour leaders assigned to a tour, as { id, name } (new array or legacy single field). */
export function tourLeadersOf(request) {
  const arr = request?.tourLeaders?.length
    ? request.tourLeaders
    : request?.tourLeaderId ? [{ id: request.tourLeaderId, name: request.tourLeaderName, email: request.tourLeaderEmail }] : [];
  return arr.map((l) => ({ id: l.id, name: l.name || l.email || 'Tour leader' }));
}

/** Teams with `leaderId`'s own teams first (order otherwise unchanged). */
export function myTeamsFirst(teams, leaderId) {
  if (!leaderId) return teams;
  return [...teams.filter((t) => t.tourLeaderId === leaderId), ...teams.filter((t) => t.tourLeaderId !== leaderId)];
}

/**
 * The teams UI, shared by the portal tab and the no-login coach page. It owns
 * only transient UI state; every change goes out through onChange(teams,
 * assignments) and the caller persists it.
 */
export function TeamsBoard({ teams, assignments, participants, canManageTeams, onChange, error, intro, tourLeaders = [], myLeaderId = null, squads = [], onLinkSquad = null }) {
  const [selected, setSelected] = useState(null); // participant id being placed
  const [showAll, setShowAll] = useState(false);
  const [newTeamName, setNewTeamName] = useState('');
  const [renaming, setRenaming] = useState(null); // { id, name }
  const [confirmDelete, setConfirmDelete] = useState(null);

  const persist = onChange;

  function addTeam() {
    const name = newTeamName.trim() || `Team ${teams.length + 1}`;
    const id = uid();
    persist([...teams, { id, name }], { ...assignments, [id]: [] });
    setNewTeamName('');
  }

  function saveRename() {
    if (!renaming) return;
    const name = renaming.name.trim();
    if (name) persist(teams.map((t) => (t.id === renaming.id ? { ...t, name } : t)), assignments);
    setRenaming(null);
  }

  function deleteTeam(teamId) {
    const nextAssignments = { ...assignments };
    delete nextAssignments[teamId];
    persist(teams.filter((t) => t.id !== teamId), nextAssignments);
    setConfirmDelete(null);
  }

  function setTeamLeader(teamId, leaderId) {
    const leader = tourLeaders.find((l) => l.id === leaderId);
    persist(teams.map((t) => (t.id === teamId
      ? { ...t, tourLeaderId: leader?.id || null, tourLeaderName: leader?.name || '' }
      : t)), assignments);
  }

  function place(teamId, participantId = selected) {
    if (!participantId) return;
    persist(teams, moveParticipant(assignments, participantId, teamId));
    setSelected(null);
  }

  const byId = useMemo(() => Object.fromEntries(participants.map((p) => [p.id, p])), [participants]);
  const pool = useMemo(
    () => participants
      .filter((p) => showAll || isPlayer(p))
      .sort((a, b) => fullName(a).localeCompare(fullName(b))),
    [participants, showAll]
  );
  const assignedIds = new Set(Object.values(assignments).flat());
  const unassigned = pool.filter((p) => !assignedIds.has(p.id));
  const selectedP = selected ? byId[selected] : null;

  const chip = (p, { inTeam } = {}) => {
    const isSel = selected === p.id;
    return (
      <div
        key={p.id}
        draggable
        onDragStart={(e) => { e.dataTransfer.setData('text/plain', p.id); setSelected(p.id); }}
        onDragEnd={() => setSelected(null)}
        onClick={(e) => { e.stopPropagation(); setSelected(isSel ? null : p.id); }}
        className={`flex items-center gap-1.5 px-2.5 py-1 border rounded-full text-[13px] font-semibold cursor-pointer select-none transition-all ${
          isSel
            ? 'bg-pb border-pb text-white shadow-md'
            : inTeam
              ? 'bg-[#F9FAFB] border-[#E5E7EB] text-[#374151] hover:border-[#93C5FD]'
              : 'bg-[#EBF2FC] border-[#93C5FD] text-pb hover:scale-105'
        }`}
        title={inTeam ? 'Tap, then tap another team to move' : 'Tap, then tap a team to place'}
      >
        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 ${
          isSel ? 'bg-white text-pb' : inTeam ? 'bg-[#6B7280] text-white' : 'bg-pb text-white'
        }`}>
          {(p.firstName || '?').charAt(0)}
        </span>
        {fullName(p)}
        {playerDetails(p) && <span className={`font-medium ${isSel ? 'text-white/80' : 'text-[#6B7280]'}`}>({playerDetails(p)})</span>}
        {!isPlayer(p) && <span className={`text-[10.5px] font-bold ${isSel ? 'text-white/80' : 'text-[#9CA3AF]'}`}>· {p.travellerType}</span>}
        {inTeam && (
          <button
            onClick={(e) => { e.stopPropagation(); place(null, p.id); }}
            className={`ml-0.5 leading-none text-[15px] ${isSel ? 'text-white/80' : 'text-[#9CA3AF] hover:text-dr'}`}
            title="Remove from team"
            aria-label={`Remove ${fullName(p)} from team`}
          >×</button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-4" onClick={() => setSelected(null)}>
      {/* Header + instructions */}
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-[14.5px] font-bold text-[#111827]">Teams</h3>
            <p className="text-[12.5px] text-[#6B7280] mt-1 max-w-[560px]">
              {intro || (canManageTeams
                ? 'Create the teams for this tour. The coach can rename them and place players, from their portal or the link below; you can too.'
                : 'Tap a player, then tap a team to place them. Tap a player already in a team to move them. Changes save automatically.')}
            </p>
          </div>
          <label className="flex items-center gap-2 text-[12.5px] text-[#374151] cursor-pointer" onClick={(e) => e.stopPropagation()}>
            <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
            Include non-players
          </label>
        </div>

        {canManageTeams && (
          <div className="flex gap-2 mt-4" onClick={(e) => e.stopPropagation()}>
            <input
              value={newTeamName}
              onChange={(e) => setNewTeamName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addTeam(); }}
              placeholder={`Team ${teams.length + 1} (e.g. U14 A, Girls, Seniors)`}
              className="flex-1 border border-[#E5E7EB] rounded-[9px] px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
            />
            <button onClick={addTeam}
              className="px-4 py-2 text-[13px] font-bold text-white bg-pb hover:bg-navy rounded-[9px] transition">
              + Add team
            </button>
          </div>
        )}
        {error && <p className="text-[12.5px] text-dr mt-3">{error}</p>}
      </div>

      {/* Unassigned */}
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider">
            Not in a team · {unassigned.length}
          </p>
          <p className="text-[11.5px] text-[#9CA3AF]">{pool.length} {showAll ? 'travellers' : 'players'}</p>
        </div>
        {pool.length === 0 ? (
          <p className="text-[13px] text-[#9CA3AF]">No {showAll ? 'travellers' : 'players'} registered on this tour yet.</p>
        ) : unassigned.length === 0 ? (
          <p className="text-[13px] text-sg font-semibold">Everyone is in a team.</p>
        ) : (
          <div
            className="flex flex-wrap gap-2"
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { const id = e.dataTransfer.getData('text/plain'); if (id) place(null, id); }}
          >
            {unassigned.map((p) => chip(p))}
          </div>
        )}
        {selectedP && (
          <p className="text-[12px] text-pb font-semibold mt-3">
            Now tap a team to place <strong>{fullName(selectedP)}</strong>
          </p>
        )}
      </div>

      {/* Teams */}
      {teams.length === 0 ? (
        <p className="text-[13px] text-[#9CA3AF] text-center py-6 border border-dashed border-[#E5E7EB] rounded-[14px] bg-white">
          No teams yet. Add the first one above.
        </p>
      ) : (
        <div className="grid grid-cols-1 1080:grid-cols-2 gap-4">
          {myTeamsFirst(teams, myLeaderId).map((team) => {
            const isMine = !!myLeaderId && team.tourLeaderId === myLeaderId;
            const members = (assignments[team.id] || []).map((pid) => byId[pid]).filter(Boolean);
            const isTarget = !!selected && !(assignments[team.id] || []).includes(selected);
            const isRenaming = renaming?.id === team.id;
            return (
              <div
                key={team.id}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { const id = e.dataTransfer.getData('text/plain'); if (id) place(team.id, id); }}
                onClick={(e) => { if (isTarget) { e.stopPropagation(); place(team.id); } }}
                className={`bg-white rounded-[14px] border-2 transition-all ${
                  isTarget ? 'border-pb cursor-pointer shadow-md' : isMine ? 'border-[#F59E0B] shadow-[0_0_0_3px_rgba(245,158,11,.15)]' : 'border-[#E5E7EB]'
                }`}
              >
                <div className={`px-5 py-3 border-b border-[#F1F3F6] flex items-center gap-2 ${isMine ? 'bg-[#FFFBEB] rounded-t-[12px]' : ''}`} onClick={(e) => isRenaming && e.stopPropagation()}>
                  {isRenaming ? (
                    <>
                      <input
                        autoFocus
                        value={renaming.name}
                        onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); if (e.key === 'Escape') setRenaming(null); }}
                        className="flex-1 border border-[#E5E7EB] rounded-[8px] px-2.5 py-1.5 text-[13px] focus:outline-none focus:ring-2 focus:ring-pb"
                      />
                      <button onClick={saveRename} className="text-[12px] font-bold text-pb hover:text-navy">Save</button>
                      <button onClick={() => setRenaming(null)} className="text-[12px] font-semibold text-[#9CA3AF]">Cancel</button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <p className="text-[14px] font-bold text-[#111827] truncate">
                          {team.name}
                          {isMine && (
                            <span className="ml-2 align-middle px-2 py-[2px] rounded-full bg-[#F59E0B] text-white text-[10.5px] font-bold">Your team</span>
                          )}
                        </p>
                        {team.ageLabel && (
                          <p className="text-[12px] font-semibold text-[#6B7280]">{team.ageLabel}</p>
                        )}
                        {team.squadRemoved && (
                          <p className="text-[11.5px] font-semibold text-[#D97706]">
                            Squad removed from Team Info{canManageTeams ? '. Delete this team if it is no longer needed.' : ''}
                          </p>
                        )}
                        <p className="text-[11px] text-[#9CA3AF]">
                          {members.length} player{members.length !== 1 ? 's' : ''}
                          {!canManageTeams && team.tourLeaderName && <> · Tour leader: <span className="font-semibold text-[#6B7280]">{team.tourLeaderName}</span></>}
                        </p>
                      </div>
                      {isTarget && <span className="text-[11px] font-bold text-pb">Tap to place</span>}
                      {!selected && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setRenaming({ id: team.id, name: team.name }); }}
                          className="text-[12px] font-semibold text-pb hover:text-navy"
                        >
                          Rename
                        </button>
                      )}
                      {!selected && canManageTeams && (
                        confirmDelete === team.id ? (
                          <span className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => deleteTeam(team.id)} className="text-[12px] font-bold text-dr">Delete</button>
                            <button onClick={() => setConfirmDelete(null)} className="text-[12px] font-semibold text-[#9CA3AF]">Keep</button>
                          </span>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); setConfirmDelete(team.id); }}
                            className="text-[12px] font-semibold text-[#9CA3AF] hover:text-dr"
                          >
                            Delete
                          </button>
                        )
                      )}
                    </>
                  )}
                </div>
                {canManageTeams && (
                  <div className="px-5 pt-3 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <span className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider">Tour leader</span>
                    {tourLeaders.length === 0 && !team.tourLeaderId ? (
                      <span className="text-[12px] text-[#9CA3AF]">Assign tour leaders to this tour first (Overview tab).</span>
                    ) : (
                      <select
                        value={team.tourLeaderId || ''}
                        onChange={(e) => setTeamLeader(team.id, e.target.value)}
                        className="border border-[#E5E7EB] rounded-[8px] px-2 py-1 text-[12.5px] text-[#374151] focus:outline-none focus:ring-2 focus:ring-pb"
                      >
                        <option value="">None</option>
                        {tourLeaders.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                        {/* Keep a leader who has since been taken off the tour visible until changed. */}
                        {team.tourLeaderId && !tourLeaders.some((l) => l.id === team.tourLeaderId) && (
                          <option value={team.tourLeaderId}>{team.tourLeaderName || 'Removed leader'} (not on tour)</option>
                        )}
                      </select>
                    )}
                  </div>
                )}
                {canManageTeams && onLinkSquad && squads.length > 0 && (
                  <div className="px-5 pt-2 flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                    <span className="text-[11px] font-bold text-[#9CA3AF] uppercase tracking-wider">Squad</span>
                    <select
                      value={team.squadRemoved ? '' : (team.squadId || '')}
                      onChange={(e) => onLinkSquad(team.id, e.target.value || null)}
                      className="border border-[#E5E7EB] rounded-[8px] px-2 py-1 text-[12.5px] text-[#374151] focus:outline-none focus:ring-2 focus:ring-pb"
                    >
                      <option value="">Not linked</option>
                      {squads
                        .filter((sq) => sq.id === team.squadId || !teams.some((t) => t.squadId === sq.id))
                        .map((sq) => <option key={sq.id} value={sq.id}>{sq.label || sq.id}</option>)}
                    </select>
                  </div>
                )}
                <div className="p-5 min-h-[64px]">
                  {members.length === 0 ? (
                    <p className={`text-[13px] text-center py-1 ${isTarget ? 'text-pb font-semibold' : 'text-[#D1D5DB]'}`}>
                      {isTarget ? '+ Place here' : 'Empty'}
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {members.map((p) => chip(p, { inTeam: true }))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Staff-only: email or copy the coach's no-login link. */
function CoachLinkBar({ requestId, request, token }) {
  const { currentUser } = useAuth();
  const [live, setLive] = useState(null);
  const [busy, setBusy] = useState('');
  const [done, setDone] = useState('');
  const [err, setErr] = useState('');
  const email = request?.customerEmail || '';

  useEffect(() => {
    let cancelled = false;
    setLive(null);
    isLiveCoachLink(token, requestId).then((v) => { if (!cancelled) setLive(v); });
    return () => { cancelled = true; };
  }, [token, requestId]);

  async function ensureLink() {
    if (token && live) return token;
    const id = await createCoachLink({ requestId, kind: 'teams', user: currentUser });
    await setDoc(teamsLayoutRef(requestId), { coachToken: id }, { merge: true });
    return id;
  }

  function flash(what) {
    setDone(what);
    setTimeout(() => setDone(''), 2500);
  }

  async function send() {
    setBusy('send'); setErr('');
    try {
      const tokenId = await ensureLink();
      await addDoc(collection(db, 'teamsNotifications'), {
        requestId,
        contactEmail: email,
        contactName: request?.contactName || '',
        tokenId,
        sentAt: serverTimestamp(),
        status: 'pending',
      });
      flash('sent');
    } catch (e) {
      console.error(e);
      setErr('Could not send the email. Please try again.');
    } finally { setBusy(''); }
  }

  async function copy() {
    if (live === null) return;
    if (!(token && live)) {
      // Create the link first; Copy is ready once it's live.
      setBusy('copy'); setErr('');
      try { await ensureLink(); } catch (e) { console.error(e); setErr('Could not create a link.'); }
      setBusy('');
      return;
    }
    navigator.clipboard.writeText(coachLinkUrl('teams', token));
    flash('copied');
  }

  async function reset() {
    setBusy('reset'); setErr('');
    try {
      if (token) await revokeCoachLink(token);
      const id = await createCoachLink({ requestId, kind: 'teams', user: currentUser });
      await setDoc(teamsLayoutRef(requestId), { coachToken: id }, { merge: true });
      flash('reset');
    } catch (e) {
      console.error(e);
      setErr('Could not reset the link.');
    } finally { setBusy(''); }
  }

  const hasLink = !!(token && live);
  return (
    <div className="bg-[#F9FAFB] rounded-[12px] border border-[#E5E7EB] p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[12.5px] font-semibold text-[#374151]">Coach link (no login needed)</p>
          <p className="text-[11.5px] text-[#9CA3AF] truncate max-w-[520px]">
            {hasLink ? coachLinkUrl('teams', token) : live === null && token ? 'Checking link…' : 'No link yet. Send or copy to create one.'}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={send}
            disabled={!email || !!busy}
            title={email ? `Emails ${email}` : 'Add a primary contact email first'}
            className="px-3 py-[7px] text-[12.5px] font-bold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-50"
          >
            {busy === 'send' ? 'Sending…' : done === 'sent' ? '✓ Sent' : 'Send to coach'}
          </button>
          <button
            onClick={copy}
            disabled={!!busy || (live === null && !!token)}
            className="px-3 py-[7px] text-[12.5px] font-bold text-pb border border-pb/30 bg-white hover:bg-blue-light rounded-[8px] transition disabled:opacity-50"
          >
            {done === 'copied' ? '✓ Copied' : hasLink ? 'Copy link' : busy === 'copy' ? 'Creating…' : 'Create link'}
          </button>
        </div>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 mt-2">
        <p className="text-[11.5px] text-[#6B7280]">
          {email ? <>Send to coach emails <strong>{email}</strong>. Copy the link to share it another way.</> : 'No primary contact email on this tour. Copy the link to share it.'}
        </p>
        {hasLink && (
          <button onClick={reset} disabled={!!busy} className="text-[11.5px] font-semibold text-[#9CA3AF] hover:text-dr disabled:opacity-50">
            {done === 'reset' ? '✓ New link created' : 'Reset link (stops the old one working)'}
          </button>
        )}
      </div>
      {err && <p className="text-[12px] text-dr mt-2">{err}</p>}
    </div>
  );
}

/** The Teams tab in the signed-in portals (staff, tour leader, customer). */
export default function TeamsTab({ requestId, request, mode = 'coach', myLeaderId = null }) {
  const { currentUser } = useAuth();
  const canManageTeams = mode === 'staff';

  // Live teams (with age labels from Team Info) — and the squad → team sync.
  const { loading, teams: liveTeams, assignments: liveAssignments, coachToken, squads } = useTourTeams(requestId, request);
  const [local, setLocal] = useState(null); // optimistic { teams, assignments } until the snapshot catches up
  const [participants, setParticipants] = useState([]);
  const [error, setError] = useState('');
  const [linkError, setLinkError] = useState('');
  const [exporting, setExporting] = useState(false);

  useEffect(() => { setLocal(null); }, [liveTeams, liveAssignments]);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'requests', requestId, 'participants'), (snap) => {
      setParticipants(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, [requestId]);

  const teams = local ? local.teams : liveTeams;
  const assignments = local ? local.assignments : liveAssignments;

  // Optimistic: update locally, then persist. merge keeps coachToken and
  // coachSyncedAt, which this tab doesn't own.
  async function persist(nextTeams, nextAssignments) {
    setLocal({ teams: nextTeams, assignments: nextAssignments });
    setError('');
    try {
      await setDoc(teamsLayoutRef(requestId), {
        teams: nextTeams,
        assignments: nextAssignments,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.email || '',
      }, { merge: true });
    } catch (err) {
      console.error('Failed to save teams', err);
      setLocal(null);
      setError('That change could not be saved. Please try again.');
    }
  }

  async function linkSquad(teamId, squadId) {
    setLinkError('');
    try {
      await linkTeamToSquad(requestId, teamId, squadId, { squads, request });
    } catch (err) {
      console.error(err);
      setLinkError('Could not link that squad. Please try again.');
    }
  }

  async function addMissingTeams() {
    setLinkError('');
    try {
      await addTeamsForUnlinkedSquads(requestId, { squads, request });
    } catch (err) {
      console.error(err);
      setLinkError('Could not add the teams. Please try again.');
    }
  }

  async function exportCategories() {
    setExporting(true);
    try {
      await downloadTeamCategoriesPng({ request, teams, assignments, participants });
    } catch (err) {
      console.error('Team Categories export failed', err);
      setLinkError('Could not create the Team Categories image. Please try again.');
    } finally {
      setExporting(false);
    }
  }

  if (loading) {
    return <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-10 text-center text-[13px] text-[#9CA3AF]">Loading…</div>;
  }

  // Coaches only see this once there are teams (from MST or Team Info).
  if (!canManageTeams && teams.length === 0) {
    return (
      <div className="bg-white rounded-[14px] border border-[#E5E7EB] p-10 text-center">
        <p className="text-[14px] font-bold text-[#111827] mb-2">Teams not set up yet</p>
        <p className="text-[13px] text-[#6B7280]">Your teams appear here once the team info form is filled in (or MST sets them up). Then you can name them and place your players.</p>
      </div>
    );
  }

  const missing = canManageTeams && teams.some((t) => !t.squadId) ? unlinkedSquads(teams, squads) : [];

  return (
    <div className="space-y-4">
      {canManageTeams && teams.length > 0 && (
        <CoachLinkBar requestId={requestId} request={request} token={coachToken} />
      )}
      {canManageTeams && teams.length > 0 && (
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-[13px] font-bold text-[#111827]">Team Categories slide</p>
            <p className="text-[12px] text-[#6B7280]">A ready-made 1920×1080 image of every team, its age group, coach (tour leader) and players — drop it straight into the Canva welcome presentation.</p>
          </div>
          <button onClick={exportCategories} disabled={exporting}
            className="px-3.5 py-[7px] text-[12.5px] font-bold text-white bg-pb hover:bg-navy rounded-[8px] transition disabled:opacity-60 shrink-0">
            {exporting ? 'Creating…' : 'Download for Canva (PNG)'}
          </button>
        </div>
      )}
      {missing.length > 0 && (
        <div className="bg-[#FFFBEB] rounded-[12px] border border-[#FCD34D] p-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-[12.5px] text-[#92400E]">
            {missing.length} squad{missing.length !== 1 ? 's' : ''} from Team Info ({missing.map((sq) => sq.label || sq.id).join(', ')}) {missing.length !== 1 ? "aren't" : "isn't"} linked to a team.
            Link {missing.length !== 1 ? 'them' : 'it'} to an existing team below, or add a team for each.
          </p>
          <button onClick={addMissingTeams}
            className="px-3 py-[7px] text-[12.5px] font-bold text-white bg-[#D97706] hover:bg-[#B45309] rounded-[8px] transition">
            Add {missing.length !== 1 ? 'teams' : 'a team'} for {missing.length !== 1 ? 'them' : 'it'}
          </button>
        </div>
      )}
      {linkError && <p className="text-[12.5px] text-dr">{linkError}</p>}
      <TeamsBoard
        teams={teams}
        assignments={assignments}
        participants={participants}
        canManageTeams={canManageTeams}
        onChange={persist}
        error={error}
        tourLeaders={canManageTeams ? tourLeadersOf(request) : []}
        myLeaderId={myLeaderId}
        squads={squads}
        onLinkSquad={canManageTeams ? linkSquad : null}
      />
    </div>
  );
}
